import { useMemo, useState } from 'react';
import { ChevronRight, FolderClosed, Search, X } from 'lucide-react';
import type { Folder, Id } from '@shared/types';
import { cn } from '@/lib/format';
import { t } from '@/lib/i18n';
import { useFolders } from '@/lib/queries';

/**
 * Destination picker for a bulk move.
 *
 * A tree rather than a flat list: the library is 8,616 folders deep on prod and
 * names repeat across brands, so "LINKED_IMAGES" alone does not identify a
 * destination — the path to it does. Filtering flattens to matching paths for
 * the same reason, showing where each hit actually sits.
 */
export function MoveDialog({
  count,
  onCancel,
  onConfirm,
  busy,
}: {
  /** How many items are being moved, for the confirm label. */
  count: number;
  onCancel: () => void;
  onConfirm: (folderId: Id) => void;
  busy?: boolean;
}) {
  const { data, isPending } = useFolders();
  const [filter, setFilter] = useState('');
  const [picked, setPicked] = useState<Folder | null>(null);

  const roots = data?.tree ?? [];

  /** Flattened matches, used only while filtering. */
  const matches = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return null;
    const out: Folder[] = [];
    const walk = (nodes: Folder[]) => {
      for (const n of nodes) {
        if (n.name.toLowerCase().includes(q) || (n.path ?? '').toLowerCase().includes(q)) out.push(n);
        walk(n.children ?? []);
        if (out.length > 200) return; // a filter this broad is not a useful list
      }
    };
    walk(roots);
    return out;
  }, [filter, roots]);

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4"
      // Clicking the backdrop cancels; clicks inside must not bubble to it.
      onClick={onCancel}
      role="dialog"
      aria-modal="true"
      aria-label={t('moveDialog.moveToFolder')}
    >
      <div
        className="flex max-h-[80vh] w-[min(92vw,32rem)] flex-col overflow-hidden rounded-xl surface-card border line shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center gap-2 border-b line px-4 py-3">
          <h2 className="flex-1 text-sm font-semibold text-strong">
            {count === 1 ? t('moveDialog.moveOneItemTo') : t('moveDialog.moveNItemsTo', { count })}
          </h2>
          <button onClick={onCancel} className="rounded p-1 text-muted hover:bg-black/5 dark:hover:bg-white/10" aria-label={t('moveDialog.close')}>
            <X className="size-4" />
          </button>
        </header>

        <div className="border-b line p-3">
          <label className="relative block">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-faint" />
            <input
              autoFocus
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder={t('moveDialog.filterFolders')}
              className="w-full rounded-lg border line surface-sunken py-1.5 pl-8 pr-3 text-[13px] outline-none focus:border-brand-500"
            />
          </label>
        </div>

        <div className="scroll-thin min-h-0 flex-1 overflow-y-auto p-2">
          {isPending ? (
            <p className="px-2 py-6 text-center text-xs text-muted">{t('moveDialog.loadingFolders')}</p>
          ) : matches ? (
            matches.length === 0 ? (
              <p className="px-2 py-6 text-center text-xs text-muted">{t('moveDialog.noMatches', { filter })}</p>
            ) : (
              matches.map((f) => (
                <FolderRow key={f.id} folder={f} picked={picked?.id === f.id} onPick={setPicked} showPath />
              ))
            )
          ) : (
            roots.map((f) => (
              <TreeNode key={f.id} folder={f} depth={0} picked={picked} onPick={setPicked} />
            ))
          )}
        </div>

        <footer className="flex items-center gap-2 border-t line px-4 py-3">
          <p className="min-w-0 flex-1 truncate text-xs text-muted" title={picked?.path ?? ''}>
            {picked ? picked.path : t('moveDialog.chooseDestination')}
          </p>
          <button onClick={onCancel} className="rounded-lg px-3 py-1.5 text-[13px] text-muted hover:bg-black/5 dark:hover:bg-white/10">
            {t('moveDialog.cancel')}
          </button>
          <button
            disabled={!picked || busy}
            onClick={() => picked && onConfirm(picked.id)}
            className="rounded-lg bg-brand-600 px-3 py-1.5 text-[13px] font-medium text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {busy ? t('moveDialog.moving') : t('moveDialog.moveHere')}
          </button>
        </footer>
      </div>
    </div>
  );
}

function TreeNode({
  folder,
  depth,
  picked,
  onPick,
}: {
  folder: Folder;
  depth: number;
  picked: Folder | null;
  onPick: (f: Folder) => void;
}) {
  const children = folder.children ?? [];
  const [open, setOpen] = useState(depth === 0);

  return (
    <div>
      <div className="flex items-center" style={{ paddingLeft: `${depth * 0.75}rem` }}>
        {children.length > 0 ? (
          <button
            onClick={() => setOpen((v) => !v)}
            className="grid size-6 shrink-0 place-items-center text-faint hover:text-strong"
            aria-label={open ? t('moveDialog.collapse') : t('moveDialog.expand')}
          >
            <ChevronRight className={cn('size-3.5 transition-transform', open && 'rotate-90')} />
          </button>
        ) : (
          <span className="size-6 shrink-0" />
        )}
        <FolderRow folder={folder} picked={picked?.id === folder.id} onPick={onPick} />
      </div>
      {open && children.map((c) => (
        <TreeNode key={c.id} folder={c} depth={depth + 1} picked={picked} onPick={onPick} />
      ))}
    </div>
  );
}

function FolderRow({
  folder,
  picked,
  onPick,
  showPath,
}: {
  folder: Folder;
  picked: boolean;
  onPick: (f: Folder) => void;
  showPath?: boolean;
}) {
  return (
    <button
      onClick={() => onPick(folder)}
      className={cn(
        'flex min-w-0 flex-1 items-center gap-2 rounded-lg px-2 py-1.5 text-left text-[13px] hover:bg-black/5 dark:hover:bg-white/10',
        picked && 'bg-brand-50 text-brand-900 dark:bg-brand-900/40 dark:text-brand-100',
      )}
    >
      <FolderClosed className="size-3.5 shrink-0 text-faint" />
      <span className="truncate">{folder.name}</span>
      {showPath && <span className="ml-auto truncate pl-2 text-[11px] text-faint">{folder.path}</span>}
    </button>
  );
}
